/**
 * react-query wiring for `/api/exam/*` (brief §exam). Query keys are
 * namespaced `["exam", …]` per `docs/CONVENTIONS.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clearExamHint, setExamHint } from "@client/app/examHint";
import { api } from "@client/api/client";
import { isCode } from "@client/lib/errors";
import type {
  AbandonResponse,
  AnswersResponse,
  CurrentResponse,
  GradeResponse,
  HistoryResponse,
  LobbyResponse,
  SaveAnswersBody,
  SaveReflectionBody,
  SaveReflectionResponse,
  SessionDetailResponse,
  StartConflictResponse,
  StartExamBody,
  StartResponse,
  SubmitResponse,
} from "./types";

export const examKeys = {
  all: ["exam"] as const,
  lobby: () => ["exam", "lobby"] as const,
  current: () => ["exam", "current"] as const,
  history: () => ["exam", "history"] as const,
  detail: (id: string) => ["exam", "detail", id] as const,
};

export function useExamLobby() {
  return useQuery({
    queryKey: examKeys.lobby(),
    queryFn: () => api.get<LobbyResponse>("/api/exam/lobby"),
  });
}

/** 404 (no active session) is a normal, expected outcome here — not an error to surface. */
export function useCurrentExam() {
  return useQuery({
    queryKey: examKeys.current(),
    queryFn: async () => {
      try {
        return await api.get<CurrentResponse>("/api/exam/current");
      } catch (error) {
        if (isCode(error, "NOT_FOUND")) return null;
        throw error;
      }
    },
  });
}

export function useStartExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StartExamBody) =>
      api.post<StartResponse | StartConflictResponse>("/api/exam/start", body),
    onSuccess: () => {
      setExamHint();
      return qc.invalidateQueries({ queryKey: examKeys.all });
    },
  });
}

export function useSaveExamAnswers(sessionId: string) {
  return useMutation({
    mutationFn: (body: SaveAnswersBody) =>
      api.put<AnswersResponse>(`/api/exam/${sessionId}/answers`, body),
  });
}

export function useSubmitExam(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SubmitResponse>(`/api/exam/${sessionId}/submit`),
    onSuccess: () => {
      clearExamHint();
      return qc.invalidateQueries({ queryKey: examKeys.all });
    },
  });
}

/** Closes the running session without handing it in (`POST /api/exam/:id/abandon`). */
export function useAbandonExam(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AbandonResponse>(`/api/exam/${sessionId}/abandon`),
    // Not returned, so `mutateAsync` settles before the `/current` refetch does.
    onSuccess: () => {
      clearExamHint();
      void qc.invalidateQueries({ queryKey: examKeys.all });
    },
  });
}

export function useGradeExam(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (selfScores: Record<string, { rubricChecked: string[] }>) =>
      api.post<GradeResponse>(`/api/exam/${sessionId}/grade`, { selfScores }),
    onSuccess: () => qc.invalidateQueries({ queryKey: examKeys.all }),
  });
}

export function useExamHistory() {
  return useQuery({
    queryKey: examKeys.history(),
    queryFn: () => api.get<HistoryResponse>("/api/exam/history"),
  });
}

export function useExamDetail(id: string | undefined) {
  return useQuery({
    queryKey: examKeys.detail(id ?? ""),
    queryFn: () => api.get<SessionDetailResponse>(`/api/exam/${id}`),
    enabled: !!id,
  });
}

/** The exam wrapper's one save: causes and plan in a single PUT (pedagogy spec §7.3.4). */
export function useSaveReflection(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveReflectionBody) =>
      api.put<SaveReflectionResponse>(`/api/exam/${sessionId}/reflection`, body),
    onSuccess: (_saved, body) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: examKeys.detail(sessionId) }),
        qc.invalidateQueries({ queryKey: examKeys.lobby() }),
        // "Lägg till i dagens pass" changes what Today serves: every Today plan the student holds
        // (Home's card and the runner) is stale. The prefix is the Today api's query key
        // (`practice/today/api.ts#todayKeys`), spelled out here so the features stay apart.
        ...(body.addToToday ? [qc.invalidateQueries({ queryKey: ["practice", "today"] })] : []),
      ]),
  });
}
